/**
 * MonthlyPaymentMatcherEs — Spanish version of the "Estimate My Monthly Payment"
 * widget. Compliant: shows NO treatment prices. Captures a lead via /api/leads
 * (lang="es") so it feeds the Spanish drip campaign + attribution.
 */
import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TEETH = [
  { v: 'un solo diente', label: 'Un diente' },
  { v: 'varios dientes', label: 'Varios dientes' },
  { v: 'una arcada completa (All-on-4)', label: 'Arcada completa / All-on-4' },
];
const SEGURO = [
  { v: 'tiene seguro dental', label: 'Sí, tengo seguro' },
  { v: 'no tiene seguro dental', label: 'No tengo seguro' },
];

const FIELD = 'w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition';
const isPhone = (p) => (p || '').replace(/\D/g, '').length >= 10;

const MonthlyPaymentMatcherEs = ({ page = '' }) => {
  const [step, setStep] = useState(1);
  const [teeth, setTeeth] = useState('');
  const [seg, setSeg] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!name.trim() || !isPhone(phone) || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setErr('Ingresa tu nombre, un teléfono válido y tu correo.');
      return;
    }
    setLoading(true);
    setErr('');
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, {
        name: name.trim(),
        phone: phone.replace(/\D/g, ''),
        email: email.trim(),
        lang: 'es',
        source: 'affordable_implants_estimator_es',
        page,
        message: `Solicitud de opciones de pago — reemplazar ${teeth || 'sin especificar'}, ${seg || 'seguro sin especificar'}. Quiere una cotización de pago mensual en su consulta gratis.`,
      });
      setDone(true);
    } catch (e) {
      setErr('Algo salió mal. Llama al (817) 985-3337.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border-l-4 border-teal-500 p-8 text-center" data-testid="matcher-es-success">
        <div className="text-4xl mb-3" aria-hidden="true">✅</div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Eres un excelente candidato para pagos mensuales bajos!</h3>
        <p className="text-gray-600 mb-6">Nuestra coordinadora de tratamiento te llamará para explicarte tus opciones de financiamiento 0% y agendar tu consulta GRATIS, donde recibirás tu pago mensual exacto — sin sorpresas.</p>
        <a href="tel:8179853337" data-testid="matcher-es-call" className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-bold px-8 py-4 rounded-lg">
          ¿Prefieres hablar ahora? Llama al (817) 985-3337
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg border-l-4 border-teal-500 p-8" data-testid="payment-matcher-es">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-2xl" aria-hidden="true">💳</span>
        <h3 className="text-2xl font-bold text-gray-900">Calcula tu Pago Mensual</h3>
      </div>
      <p className="text-sm text-gray-500 mb-6">2 preguntas rápidas — sin costo ni compromiso. Paso {step} de 3.</p>

      {step === 1 && (
        <div data-testid="matcher-es-step-1">
          <p className="font-semibold text-gray-800 mb-3">¿Qué necesitas reemplazar?</p>
          <div className="grid gap-3">
            {TEETH.map((t) => (
              <button key={t.v} data-testid={`teeth-es-${t.label}`} onClick={() => { setTeeth(t.v); setStep(2); }}
                className="text-left border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 rounded-lg px-5 py-3 font-medium text-gray-800 transition-colors">
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="matcher-es-step-2">
          <p className="font-semibold text-gray-800 mb-3">¿Tienes seguro dental?</p>
          <div className="grid gap-3">
            {SEGURO.map((s) => (
              <button key={s.v} data-testid={`ins-es-${s.label}`} onClick={() => { setSeg(s.v); setStep(3); }}
                className="text-left border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 rounded-lg px-5 py-3 font-medium text-gray-800 transition-colors">
                {s.label}
              </button>
            ))}
          </div>
          <button onClick={() => setStep(1)} className="mt-4 text-sm text-teal-600 hover:underline">← Atrás</button>
        </div>
      )}

      {step === 3 && (
        <div data-testid="matcher-es-step-3">
          <p className="font-semibold text-gray-800 mb-1">¡Casi listo!</p>
          <p className="text-sm text-gray-500 mb-4">¿A dónde te enviamos tus opciones de pago personalizadas?</p>
          <div className="space-y-3">
            <input data-testid="matcher-es-name" className={FIELD} value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" maxLength={100} />
            <input data-testid="matcher-es-phone" type="tel" className={FIELD} value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d\s\-()]/g, ''))} placeholder="(817) 555-1234" maxLength={20} />
            <input data-testid="matcher-es-email" type="email" className={FIELD} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" maxLength={100} />
            {err && <p className="text-red-500 text-sm">{err}</p>}
            <button data-testid="matcher-es-submit" onClick={submit} disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3 font-bold rounded-md disabled:opacity-60">
              {loading ? 'Enviando…' : 'Ver mis opciones de pago →'}
            </button>
          </div>
          <button onClick={() => setStep(2)} className="mt-4 text-sm text-teal-600 hover:underline">← Atrás</button>
          <p className="text-xs text-gray-400 mt-3">Nunca mostramos precios genéricos — cada cotización es personalizada en tu consulta gratis. Tu información está segura.</p>
        </div>
      )}
    </div>
  );
};

export default MonthlyPaymentMatcherEs;
